import _ from 'lodash';

import { bindServices } from '@/services/api';
import { toPaginate } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';

const api = bindServices('aiSkills');

const namespace = 'aiSkills';
const fetchRoute = 'ai/skill';
const fetchParams = 'AiSkill';

const initialPagination = { page: 1, count: 0, totalPages: 0 };

const initialState = {
  list: [],
  pagination: initialPagination,
  listCache: null,
  orderBy: '',
  select: CoreQueries[fetchParams].paged.select,
  searchSelect: CoreQueries[fetchParams].paged.searchSelect,
  showForm: false,
  showImportModal: false,
  importSkillId: null,
  importSourceProvider: null,
  importPreviewResult: null,
  detail: false,
  id: null,
  item: null,
  success: false,
  validationErrors: [],
};

const AiSkillModel = {
  namespace,
  state: initialState,

  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const pageSize = yield select((state) => state.global.pageSize);
        const limit = payload.limit ?? pageSize;
        const page = payload.page ?? 1;
        const cacheKey = `skills-${page}-${limit}-${payload?.search || ''}-${
          payload?.filter || ''
        }`;

        const state = yield select((s) => s.aiSkills);
        if (state.listCache && state.listCache.cacheKey === cacheKey) {
          yield put({
            type: 'queryData',
            payload: {
              ...state.listCache.response,
              page,
              limit,
              cacheKey,
            },
          });
          return;
        }

        const params = {
          ...CoreQueries[fetchParams].paged,
          pageSize,
          ...payload,
        };
        const res = yield call(api.getAll, {
          route: `${fetchRoute}/paged`,
          params,
          readPriority: true,
        });
        yield put({
          type: 'queryData',
          payload: {
            ...res,
            limit,
            page,
            cacheKey,
          },
        });
      } catch (e) {
        /*  */
      }
    },

    *fetchItem({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: false });
        const res = yield call(api.getById, {
          route: fetchRoute,
          id: payload.id,
          params: payload.detail
            ? CoreQueries[fetchParams]?.itemDetail
            : { ...CoreQueries[fetchParams]?.item, ...(payload?.params && { ...payload.params }) },
        });
        yield put({ type: 'queryItem', payload: res });
      } catch (e) {
        /*  */
      }
    },

    *fetchCreate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        const res = yield call(api.create, {
          route: fetchRoute,
          data: payload.data,
        });
        yield put({ type: 'clearListCache' });

        // Surface the freshly created skill in `list` so the EntitySelector's
        // inline-create flow can auto-select it: the picker reads
        // `state.aiSkills.list` (its `listSelector`) right after `success`
        // flips to true and selects the first item that isn't already chosen.
        // We merge the submitted form data with the API response so we always
        // have at least `id` + `name`, regardless of the response envelope.
        const created = res?.data || res || {};
        const createdItem = { ...payload.data, ...created };
        if (createdItem.id) {
          yield put({ type: 'prependCreated', payload: createdItem });
        }

        yield put({
          type: 'success',
          payload: { success: true },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchUpdate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        yield call(api.update, {
          route: fetchRoute,
          data: payload.data,
        });
        yield put({ type: 'clearListCache' });
        yield put({
          type: 'success',
          payload: { success: true },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchDelete({ payload }, { call, put }) {
      try {
        const { id, view } = payload;
        yield put({
          type: 'global/modalConfirm',
          payload: { state: false, id: null },
        });
        if (view) {
          yield put({
            type: 'queryShowForm',
            payload: {
              state: false,
              item: null,
              detail: false,
              id: null,
            },
          });
        }
        yield call(api.remove, { route: fetchRoute, id });
        yield put({ type: 'clearListCache' });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *fetchActivate({ payload }, { put, call }) {
      try {
        yield call(api.activate, {
          route: fetchRoute,
          id: payload.id,
          isActive: payload.state,
        });
        yield put({ type: 'aiSkillActivate', payload });
      } catch (e) {
        /*  */
      }
    },

    *fetchPublish({ payload }, { put, call }) {
      try {
        yield call(api.apiRoute, {
          route: `${fetchRoute}/publish/${payload.id}`,
          method: 'post',
        });
        yield put({ type: 'clearListCache' });
        yield put({ type: 'aiSkillPublish', payload: { id: payload.id } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        /*  */
      }
    },

    *fetchImportPreview({ payload }, { call, put }) {
      try {
        const body =
          payload.provider === 1
            ? { provider: payload.provider, githubTreeUrl: payload.githubTreeUrl }
            : {
                provider: payload.provider,
                zipContent: payload.zipContent,
                ...(payload.owner != null && payload.owner !== '' && { owner: payload.owner }),
              };
        const res = yield call(api.apiRoute, {
          route: `${fetchRoute}/import/preview`,
          method: 'post',
          data: body,
        });
        yield put({ type: 'importPreviewResult', payload: res });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
    },

    *fetchImport({ payload }, { call, put, select }) {
      try {
        const importSkillId = yield select((state) => state.aiSkills?.importSkillId);
        const body =
          payload.provider === 1
            ? { provider: payload.provider, githubTreeUrl: payload.githubTreeUrl }
            : {
                provider: payload.provider,
                zipContent: payload.zipContent,
                ...(payload.owner != null && payload.owner !== '' && { owner: payload.owner }),
              };
        const route = importSkillId
          ? `${fetchRoute}/import/${importSkillId}`
          : `${fetchRoute}/import`;
        yield call(api.apiRoute, {
          route,
          method: 'post',
          data: body,
        });
        yield put({ type: 'closeImportModal' });
        yield put({ type: 'clearListCache' });
        yield put({ type: 'fetchList', payload: {} });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
    },

    *showForm({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
      if (!_.isNil(payload.id)) {
        yield put({ type: 'editOrDetail', payload });
      }
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'queryOrderBy', payload });
    },

    *setPaginationPage({ payload }, { put }) {
      yield put({ type: 'queryPaginationPage', payload });
    },

    *changeSuccess({ payload }, { put }) {
      yield put({ type: 'success', payload });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },
  },

  reducers: {
    queryData(state, { payload }) {
      const { cacheKey, ...rest } = payload;
      const rawItems = rest.items || payload.items || [];
      const list = rawItems.map((item) => ({
        ...item,
        isActive: item.isActive === true,
      }));
      const nextState = {
        ...state,
        list,
        pagination: _.merge(state.pagination, toPaginate(rest.items ? rest : payload)),
      };
      if (cacheKey) {
        nextState.listCache = {
          cacheKey,
          response: {
            ...rest,
            items: list,
            limit: payload.limit,
            page: payload.page,
          },
        };
      }
      return nextState;
    },

    aiSkillActivate(state, { payload }) {
      const list = state.list.map((item) =>
        item.id === payload.id ? { ...item, isActive: payload.state } : item,
      );
      const nextState = { ...state, list };
      if (nextState.listCache?.response?.items) {
        nextState.listCache = {
          ...nextState.listCache,
          response: {
            ...nextState.listCache.response,
            items: nextState.listCache.response.items.map((item) =>
              item.id === payload.id ? { ...item, isActive: payload.state } : item,
            ),
          },
        };
      }
      return nextState;
    },

    aiSkillPublish(state, { payload }) {
      const list = state.list.map((item) =>
        item.id === payload.id ? { ...item, published: true } : item,
      );
      const nextState = { ...state, list };
      if (nextState.listCache?.response?.items) {
        nextState.listCache = {
          ...nextState.listCache,
          response: {
            ...nextState.listCache.response,
            items: nextState.listCache.response.items.map((item) =>
              item.id === payload.id ? { ...item, published: true } : item,
            ),
          },
        };
      }
      return nextState;
    },

    queryItem(state, { payload }) {
      return {
        ...state,
        item: payload,
      };
    },

    queryShowForm(state, { payload }) {
      return {
        ...state,
        showForm: payload.state,
        detail: payload.detail,
        id: payload.id,
        item: payload.item ?? null,
        success: false,
        validationErrors: [],
      };
    },

    openImportModal(state, { payload }) {
      return {
        ...state,
        showImportModal: true,
        importSkillId: payload?.skillId ?? null,
        importSourceProvider: payload?.sourceProvider ?? null,
      };
    },

    closeImportModal(state) {
      return {
        ...state,
        showImportModal: false,
        importSkillId: null,
        importSourceProvider: null,
        importPreviewResult: null,
      };
    },

    closePreviewModal(state) {
      return { ...state, importPreviewResult: null };
    },

    importPreviewResult(state, { payload }) {
      return { ...state, importPreviewResult: payload };
    },

    editOrDetail(state, { payload }) {
      return {
        ...state,
        item: payload.item ?? {},
      };
    },

    queryOrderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },

    queryPaginationPage(state, { payload }) {
      return {
        ...state,
        pagination: { ...state.pagination, page: payload },
      };
    },

    success(state, { payload }) {
      return {
        ...state,
        ...payload,
        success: payload?.success ?? payload,
        validationErrors: [],
      };
    },

    errors(state, { payload }) {
      return {
        ...state,
        validationErrors: payload.validationErrors || [],
        message: payload.message,
      };
    },

    prependCreated(state, { payload }) {
      const created = {
        ...payload,
        isActive: payload.isActive !== false,
      };
      const withoutDup = state.list.filter((item) => item.id !== created.id);
      return { ...state, list: [created, ...withoutDup] };
    },

    clearListCache(state) {
      return { ...state, listCache: null };
    },

    reset() {
      return { ...initialState };
    },
  },
};

export default AiSkillModel;
